import * as yup from "yup";
import { useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Loaing from "../icons/loaing";
import CalendarIcon from "../icons/calendar";
import { Controller } from "react-hook-form";
import postEvent from "../services/eventService";
import FormBorder from "../components/formBorder";
import ConfirmModal from "../components/ConfirmModal";
import { postSafety } from "../services/safetyService";
import TextArea from "../components/event-selection/textArea";
import SelectElement from "../components/event-selection/selectElement";
import SelectAndQmark from "../components/event-selection/select&Qmark";
import moment from "moment";
import CustomCheckboxes from "../components/event-selection/customCheckboxes";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { SafetyEventsPDF } from "../components/dashboard/safety-events-pdf";

export type SafetyTypes = {
  reporting: string;
  eventDate: string;
  eventLocation: string;
  area: string;
  description: string;
  immediateConsequencesArray?: string[];
  immediateConsequences: string;
  consequencesHad: string;
  supervisionRateRespected: string;
  usualTeam: string;
  immediateCorrectiveActionArray?: string[];
  immediateCorrectiveAction: string;
  riskRelatedEventArray?: string[];
  riskRelatedEvent: string;
  eventAvoidedSaferEquipment: string;
  recurrence: string;
  origin: string;
  mistreatmentEvidence: string;
  insuranceBeenNotified: string;
  localAuthorityBeenNotified: string;
  professionalNames: string;
  printeventDate?: boolean;
  printeventLocation?: boolean;
  printarea?: boolean;
  printdescription?: boolean;
  printimmediateConsequences?: boolean;
  printimmediateConsequencesArray?: boolean;
  printconsequencesHad?: boolean;
  printsupervisionRateRespected?: boolean;
  printusualTeam?: boolean;
  printimmediateCorrectiveAction?: boolean;
  printimmediateCorrectiveActionArray?: boolean;
  printriskRelatedEvent?: boolean;
  printriskRelatedEventArray?: boolean;
  printeventAvoidedSaferEquipment?: boolean;
  printrecurrence?: boolean;
  printprofessionalNames?: boolean;
  printorigin?: boolean;
  printmistreatmentEvidence?: boolean;
  printinsuranceBeenNotified?: boolean;
  printlocalAuthorityBeenNotified?: boolean;
};

const schema = yup.object({
  eventDate: yup.date().required(),
  eventLocation: yup.string().required(),
  area: yup.string().required(),
  description: yup.string().required(),
  immediateConsequencesArray: yup
    .array()
    .min(1, "immediateConsequencesArray is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  consequencesHad: yup.string().required(),
  supervisionRateRespected: yup.string().required(),
  usualTeam: yup.string().required(),
  professionalNames: yup.string().required(),
  immediateCorrectiveActionArray: yup
    .array()
    .min(1, "immediateCorrectiveActions is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  riskRelatedEventArray: yup
    .array()
    .min(1, "riskRelatedEvent is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  eventAvoidedSaferEquipment: yup.string().required(),
  recurrence: yup.string().required(),
  origin: yup.string().required(),
  mistreatmentEvidence: yup.string().required(),
  insuranceBeenNotified: yup.string().required(),
  localAuthorityBeenNotified: yup.string().required(),
});

const SafetyEvents = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<null | Date>(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pdfData, setPdfData] = useState<any>(null);

  const handleDownload: SubmitHandler<SafetyTypes> = async (data) => {
    data.immediateConsequences =
      data.immediateConsequencesArray?.map((value) => value).join("-,-") ?? "";
    data.immediateCorrectiveAction =
      data.immediateCorrectiveActionArray?.map((value) => value).join("-,-") ??
      "";
    data.riskRelatedEvent =
      data.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
    data.eventDate = moment(data.eventDate).format("YYYY-MM-DD");

    data.printriskRelatedEvent = data.printriskRelatedEventArray;
    data.printimmediateConsequences = data.printimmediateConsequencesArray;
    data.printimmediateCorrectiveAction =
      data.printimmediateCorrectiveActionArray;

    setPdfData(data);
  };

  const resetForm = () => {
    setConfirmModalVisible(false);
    setPdfData(null);
    reset();
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<SafetyTypes>({ resolver: yupResolver(schema) });

  const onSubmit: SubmitHandler<SafetyTypes> = async (data) => {
    setLoading(true);
    try {
      const {
        printarea,
        printconsequencesHad,
        printdescription,
        printeventAvoidedSaferEquipment,
        printeventDate,
        printeventLocation,
        printimmediateConsequences,
        printimmediateConsequencesArray,
        printimmediateCorrectiveAction,
        printimmediateCorrectiveActionArray,
        printinsuranceBeenNotified,
        printlocalAuthorityBeenNotified,
        printmistreatmentEvidence,
        printorigin,
        printrecurrence,
        printsupervisionRateRespected,
        printusualTeam,
        printriskRelatedEvent,
        printriskRelatedEventArray,
        printprofessionalNames,
        ...rest
      } = data;
      rest.immediateConsequences =
        rest.immediateConsequencesArray?.map((value) => value).join("-,-") ??
        "";
      rest.immediateCorrectiveAction =
        rest.immediateCorrectiveActionArray
          ?.map((value) => value)
          .join("-,-") ?? "";
      rest.riskRelatedEvent =
        rest.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
      rest.eventDate = moment(rest.eventDate).format("YYYY-MM-DD");
      delete rest.immediateConsequencesArray;
      delete rest.immediateCorrectiveActionArray;
      delete rest.riskRelatedEventArray;

      const response = await postSafety({ ...rest });

      if (response.message === "created") {
        // TODO: needs test
        var severity = response.data.mistreatmentEvidence === "yes" ? "4" : "1";
        const eventpost = await postEvent({
          eventId: response.data._id,
          severity,
          eventDetailsDate: response?.data?.eventDate,
        });
        if ((eventpost.message = "created")) {
          toast.success("Safety Event created succesfully!");
          setLoading(false);
          navigate("/confirm");
        }
      }
      if (response.message === "created") {
        navigate("/confirm");
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const { t } = useTranslation();

  return (
    <section className="relative font-Poppins px-[20px] lg:px-0">
      <div>
        <div className="grid gap-3 lg:mx-10">
          <div className="w-16 h-16">
            <img
              src="/images/report.png"
              alt="!"
              className="max-w-full h-auto"
            />
          </div>
          <h1 className="text-4xl font-bold">
            <Trans i18nKey="Other Safety Events" t={t}>
              Other Safety Events
            </Trans>
          </h1>
          <p className="text-xl text-[#6B6B6B]">
            <Trans i18nKey="safety_event_description">
              Please report here other serious events or near miss events
              involving safety of the children
            </Trans>
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-end lg:px-[20px] my-5 lg:my-10">
          <div className="flex justify-end gap-[15px] lg:mx-[20px] my-5 lg:my-10">
            <button
              type="button"
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={() => setConfirmModalVisible(true)}
            >
              <Trans t={t}>Reset</Trans>
            </button>
            {pdfData ? (
              <PDFDownloadLink
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                document={<SafetyEventsPDF formData={pdfData ?? {}} />}
                fileName={"other-safety-events.pdf"}
              >
                {({ blob, url, loading, error }) =>
                  loading ? "loading..." : "Download PDF"
                }
              </PDFDownloadLink>
            ) : (
              <button
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                onClick={handleSubmit(handleDownload)}
              >
                <Trans>Generate PDF</Trans>
              </button>
            )}
          </div>
        </div>
        <FormBorder>
          <div className="grid gap-5">
            <div className="grid md:grid-cols-2 grid-cols-1 w-full gap-[5px] md:gap-0 ">
              <label
                htmlFor="eventDate"
                className="font-normal text-xl text-[#1E408E]"
              >
                <Trans t={t}>Safety incident date</Trans>
              </label>
              <div className="pr-10 grid">
                <div className="flex ">
                  <div className="mr-4 w-full bg-white flex items-center border border-[#8692A6] h-12 p-2 rounded">
                    <Controller
                      control={control}
                      // register={register}
                      {...register(`eventDate`)}
                      name="eventDate"
                      render={({ field }) => (
                        <DatePicker
                          filterDate={(d) => {
                            return new Date() > d;
                          }}
                          selectsRange={false}
                          onChange={(date: Date) => {
                            field.onChange(date);
                            setStartDate(date);
                          }}
                          placeholderText="mm/dd/yyyy"
                          selected={startDate}
                          dateFormat={"dd MMM yyyy"}
                          isClearable={false}
                          className="w-full border-none p-0 bg-white focus:outline-none"
                        />
                      )}
                    />
                    <CalendarIcon />
                  </div>
                  <input
                    type="checkbox"
                    {...register("printeventDate")}
                    defaultChecked
                    id={"printeventDate"}
                  />
                </div>
                {errors["eventDate"] && (
                  <p className="text-red-500 mt-2 text-sm">
                    {errors["eventDate"]?.message}
                  </p>
                )}
              </div>
            </div>
            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="eventLocation"
              label="Safety incident location"
              options={[
                "Safety interior (School indoor premise)",
                "Exterior space (School outdoor premise)",
                "Safety exterior space (School outdoor premise)",
              ]}
              translateId="Safety incident location"
            />
            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="origin"
              label="origin"
              options={[
                "Team (negligence, non respect of procedure...)",
                "Infra (broken furniture, faulty equipment...)",
                "Other",
              ]}
              translateId="origin"
            />
            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="mistreatmentEvidence"
              label="Is there any suspicion or evidence of abuse of one or more children? = child mistreatment"
              options={["yes", "no"]}
              translateId="mistreatmentEvidence"
              i18nKey="Help Box - Other Safety Event - Is there any suspicion or evidence of child mistreatment ?"
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="area"
              label="Safety incident detailed location"
              options={[
                "Playroom/activity room/classroom",
                "Reception area, hallways, walkway",
                "Restrooms",
                "Garden",
                "Technical area (kitchen, laundry room, storage room...)",
                "Space reserved for professionals",
                "Non applicable",
              ]}
              translateId="Safety incident detailed location"
            />

            <TextArea
              printEnabled
              register={register}
              errors={errors}
              name="description"
              label="Short description about the Safety incident"
              translateId="description"
            />

            <CustomCheckboxes
              printEnabled
              register={register}
              errors={errors}
              name="immediateConsequencesArray"
              options={[
                {
                  text: "Personal injury to children and/or adults",
                },
                {
                  text: "Intrusion",
                },
                {
                  text: "Property damage",
                },
                {
                  text: "Temporary partial or total closure",
                },
                {
                  text: "No immediate consequences",
                },
                { text: "Other (specify in description)" },
              ]}
              label="The immediate consequences of the Safety incident"
            />
            <CustomCheckboxes
              printEnabled
              register={register}
              errors={errors}
              name="immediateCorrectiveActionArray"
              options={[
                {
                  text: "Evacuation",
                },
                {
                  text: "Safety (containment)",
                },
                {
                  text: "Rearrangement of the space",
                },
                {
                  text: "Awarness for the team, reminder of the protocol(s)",
                },
                {
                  text: "Review of the field organization",
                },
                { text: "Review of the schedules" },
                {
                  text: "Removal of the object/furniture that caused the accident or the risk of accident",
                },
                {
                  text: "Locking down of the risk area / securing pending maintenance/work intervention",
                },
                {
                  text: "Emergency intervention for corrective repair/maintenance (e.g. window, anti-twist, sharp edges, etc.)",
                },
                {
                  text: "Others",
                },
              ]}
              label="Immediate corrective actions"
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="insuranceBeenNotified"
              label="Has the insurance claim been filed?"
              options={["yes", "no"]}
              translateId="insuranceBeenNotifiedGuide"
            />

            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="localAuthorityBeenNotified"
              label="Has the local authority been notified?"
              options={["yes", "no"]}
              translateId="localAuthorityBeenNotified"
              i18nKey="Help Box - Other Safety Event - Has the local authority been informed?"
            />

            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="supervisionRateRespected"
              label="supervisionRateRespected"
              options={["yes", "no"]}
              translateId="supervisionRateRespected"
              i18nKey="Help Box - Other Safety Event - When the event happened, was the staff ratio in compliance with regulations?"
            />

            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="usualTeam"
              label="Usual team in the reception area?"
              options={["yes", "no"]}
              translateId="usualTeam"
            />

            <TextArea
              register={register}
              errors={errors}
              printEnabled
              name="professionalNames"
              label="Name of all staff involved when the incident took place"
              translateId="professionalNames"
            />

            <CustomCheckboxes
              printEnabled
              register={register}
              errors={errors}
              name="riskRelatedEventArray"
              options={[
                {
                  text: "Risk of parents negatively influencing other parents in the school",
                },
                {
                  text: "Risk that parents will give bad publicity (social networks, negative comments on the internet)",
                },
                {
                  text: "Legal risk (lawsuit)",
                },
                {
                  text: "Risk of partial closure of the school",
                },
                {
                  text: "Risk of temporary closure of the school",
                },
                { text: "No" },
              ]}
              label="Outcomes related to the Safety incident"
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="eventAvoidedSaferEquipment"
              label="Could the Safety incident be avoided with safer equipment?"
              options={["yes", "no", "Not applicable"]}
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="consequencesHad"
              label="Possible outcome arising from the Safety incident"
              options={[
                "Personal injury to children and/or adults",
                "Intrusion",
                "Property damage",
                "Other (specify in description)",
              ]}
              translateId="Possible outcome arising from the Safety incident"
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="recurrence"
              label="Recurrence"
              options={[
                "No, isolated incident",
                "Yes, same type of event happened in the last 3 months",
              ]}
              translateId="Recurrence"
            />
          </div>
        </FormBorder>
        <div className="flex justify-end lg:px-[20px] mt-10">
          <button
            type={"submit"}
            className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
          >
            <Trans i18nKey="Submit">Submit</Trans>
          </button>
          {loading && <Loaing />}
        </div>
      </form>
      <ConfirmModal
        isOpen={confirmModalVisible}
        setIsOpen={setConfirmModalVisible}
        onConfirm={resetForm}
      />
    </section>
  );
};

export default SafetyEvents;
